import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { format } from "date-fns";

const Container = styled.div`
  margin-bottom: 2rem;
`;

const SearchBar = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 0.75rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.625rem 1rem;
  padding-left: 2.25rem;
  padding-right: 5.5rem;
  border: 1.5px solid var(--color-border);
  border-radius: 8px;
  font-size: 0.925rem;
  transition: all 0.2s ease;
  background: var(--color-white);

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }

  &::placeholder {
    color: var(--color-text-light);
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-light);
  pointer-events: none;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FilterButton = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: ${(props) =>
    props.active ? "var(--color-accent)" : "transparent"};
  color: ${(props) => (props.active ? "white" : "var(--color-accent)")};
  border: 1.5px solid var(--color-accent);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.active ? "var(--color-accent-hover)" : "rgba(199, 54, 89, 0.1)"};
    border-color: ${(props) =>
      props.active ? "var(--color-accent-hover)" : "var(--color-accent)"};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FilterPanel = styled.div`
  background: var(--color-white);
  border: 1.5px solid var(--color-border);
  border-radius: 10px;
  padding: 1.25rem;
  margin-top: 0.75rem;
  display: ${(props) => (props.show ? "block" : "none")};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const FilterSection = styled.div`
  margin-bottom: 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-light);
  margin-bottom: 0.625rem;
`;

const FilterOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

const FilterChip = styled.button`
  padding: 0.375rem 0.875rem;
  background: ${(props) =>
    props.active ? "var(--color-accent)" : "transparent"};
  color: ${(props) => (props.active ? "white" : "var(--color-text)")};
  border: 1.5px solid
    ${(props) => (props.active ? "var(--color-accent)" : "var(--color-border)")};
  border-radius: 20px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.active ? "var(--color-accent-hover)" : "var(--color-bg)"};
    border-color: ${(props) =>
      props.active ? "var(--color-accent-hover)" : "var(--color-accent)"};
  }
`;

const DateFilterGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  font-size: 0.825rem;
`;

const DateInput = styled.input`
  padding: 0.375rem 0.75rem;
  border: 1.5px solid var(--color-border);
  border-radius: 6px;
  font-size: 0.8rem;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const ClearButton = styled.button`
  padding: 0.375rem 0.75rem;
  background: transparent;
  color: var(--color-accent);
  border: none;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: var(--color-accent-hover);
    text-decoration: underline;
  }
`;

const ActiveFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-bottom: 0.75rem;
`;

const ActiveFilterChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: transparent;
  color: var(--color-accent);
  border: 1.5px solid var(--color-accent);
  border-radius: 20px;
  font-size: 0.8rem;

  button {
    background: none;
    border: none;
    color: var(--color-accent);
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;

    &:hover {
      opacity: 0.7;
    }

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

// Icons
const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <title>Search</title>
    <circle cx="11" cy="11" r="8"></circle>
    <path d="m21 21-4.35-4.35"></path>
  </svg>
);

const FilterIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <title>Filter</title>
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
  </svg>
);

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <title>Close</title>
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const SearchFilter = ({ posts, onFilterChange }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [sortBy, setSortBy] = useState("newest");
  const filterPanelRef = useRef(null);

  // Extract unique categories from posts
  const categories = [
    ...new Set(posts.map((post) => post.category).filter(Boolean)),
  ];

  // Date filter presets
  const datePresets = [
    { label: "Last Week", value: "week" },
    { label: "Last Month", value: "month" },
    { label: "Last 3 Months", value: "3months" },
    { label: "Last Year", value: "year" },
  ];

  // Apply filters whenever they change
  useEffect(() => {
    const filteredPosts = posts.filter((post) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          post.title?.toLowerCase().includes(query) ||
          post.excerpt?.toLowerCase().includes(query) ||
          post.content?.toLowerCase().includes(query) ||
          post.category?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Category filter
      if (selectedCategories.length > 0) {
        if (!selectedCategories.includes(post.category)) return false;
      }

      // Date filter
      if (dateRange.start || dateRange.end) {
        const postDate = post.createdAt?.toDate
          ? post.createdAt.toDate()
          : new Date(post.createdAt);

        if (dateRange.start && postDate < new Date(dateRange.start))
          return false;
        if (dateRange.end && postDate > new Date(dateRange.end)) return false;
      }

      return true;
    });

    // Sort posts
    const sortedPosts = [...filteredPosts].sort((a, b) => {
      // Always keep pinned posts at top
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;

      const dateA = a.createdAt?.toDate
        ? a.createdAt.toDate()
        : new Date(a.createdAt);
      const dateB = b.createdAt?.toDate
        ? b.createdAt.toDate()
        : new Date(b.createdAt);

      switch (sortBy) {
        case "oldest":
          return dateA - dateB;
        case "newest":
        default:
          return dateB - dateA;
      }
    });

    onFilterChange(sortedPosts);
  }, [
    searchQuery,
    selectedCategories,
    dateRange,
    sortBy,
    posts,
    onFilterChange,
  ]);

  const handleCategoryToggle = (category) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const handleDatePreset = (preset) => {
    const now = new Date();
    let start = new Date();

    switch (preset) {
      case "week":
        start.setDate(now.getDate() - 7);
        break;
      case "month":
        start.setMonth(now.getMonth() - 1);
        break;
      case "3months":
        start.setMonth(now.getMonth() - 3);
        break;
      case "year":
        start.setFullYear(now.getFullYear() - 1);
        break;
      default:
        break;
    }

    setDateRange({
      start: format(start, "yyyy-MM-dd"),
      end: format(now, "yyyy-MM-dd"),
    });
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setSelectedCategories([]);
    setDateRange({ start: "", end: "" });
    setSortBy("newest");
  };

  const removeCategory = (category) => {
    setSelectedCategories((prev) => prev.filter((c) => c !== category));
  };

  const hasActiveFilters =
    searchQuery ||
    selectedCategories.length > 0 ||
    dateRange.start ||
    dateRange.end ||
    sortBy !== "newest";

  return (
    <Container>
      <SearchBar>
        <SearchIconWrapper>
          <SearchIcon />
        </SearchIconWrapper>
        <SearchInput
          type="text"
          placeholder="Search monologues by title, content, or category..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <FilterButton
          type="button"
          active={showFilters}
          onClick={() => setShowFilters(!showFilters)}
          title="Filter posts"
        >
          <FilterIcon />
        </FilterButton>
      </SearchBar>

      {hasActiveFilters && (
        <ActiveFilters>
          {searchQuery && (
            <ActiveFilterChip>
              Search: "{searchQuery}"
              <button type="button" onClick={() => setSearchQuery("")}>
                \n <CloseIcon />
              </button>
            </ActiveFilterChip>
          )}
          {selectedCategories.map((category) => (
            <ActiveFilterChip key={category}>
              {category}
              <button type="button" onClick={() => removeCategory(category)}>
                <CloseIcon />
              </button>
            </ActiveFilterChip>
          ))}
          {(dateRange.start || dateRange.end) && (
            <ActiveFilterChip>
              {dateRange.start && dateRange.end
                ? `${format(
                    new Date(dateRange.start),
                    "MMM d, yyyy"
                  )} - ${format(new Date(dateRange.end), "MMM d, yyyy")}`
                : dateRange.start
                ? `From ${format(new Date(dateRange.start), "MMM d, yyyy")}`
                : `Until ${format(new Date(dateRange.end), "MMM d, yyyy")}`}
              <button
                type="button"
                onClick={() => setDateRange({ start: "", end: "" })}
              >
                <CloseIcon />
              </button>
            </ActiveFilterChip>
          )}
          {sortBy !== "newest" && (
            <ActiveFilterChip>
              Sort: {sortBy === "oldest" ? "Oldest first" : sortBy}
              <button type="button" onClick={() => setSortBy("newest")}>
                \n <CloseIcon />
              </button>
            </ActiveFilterChip>
          )}
          <ClearButton type="button" onClick={clearAllFilters}>
            Clear all
          </ClearButton>
        </ActiveFilters>
      )}

      <FilterPanel show={showFilters} ref={filterPanelRef}>
        {categories.length > 0 && (
          <FilterSection>
            <FilterTitle>Categories</FilterTitle>
            <FilterOptions>
              {categories.map((category) => (
                <FilterChip
                  key={category}
                  type="button"
                  active={selectedCategories.includes(category)}
                  onClick={() => handleCategoryToggle(category)}
                >
                  {category}
                </FilterChip>
              ))}
            </FilterOptions>
          </FilterSection>
        )}

        <FilterSection>
          <FilterTitle>Date Range</FilterTitle>
          <FilterOptions>
            {datePresets.map((preset) => (
              <FilterChip
                key={preset.value}
                type="button"
                onClick={() => handleDatePreset(preset.value)}
              >
                {preset.label}
              </FilterChip>
            ))}
          </FilterOptions>
          <DateFilterGroup style={{ marginTop: "0.75rem" }}>
            <DateInput
              type="date"
              value={dateRange.start}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, start: e.target.value }))
              }
              placeholder="Start date"
            />
            <span>to</span>
            <DateInput
              type="date"
              value={dateRange.end}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, end: e.target.value }))
              }
              placeholder="End date"
            />
          </DateFilterGroup>
        </FilterSection>

        <FilterSection>
          <FilterTitle>Sort By</FilterTitle>
          <FilterOptions>
            <FilterChip
              type="button"
              active={sortBy === "newest"}
              onClick={() => setSortBy("newest")}
            >
              Newest First
            </FilterChip>
            <FilterChip
              type="button"
              active={sortBy === "oldest"}
              onClick={() => setSortBy("oldest")}
            >
              Oldest First
            </FilterChip>
          </FilterOptions>
        </FilterSection>
      </FilterPanel>
    </Container>
  );
};

export default SearchFilter;
